import React, { useState } from 'react';
import { Button, FieldGroup, ButtonGroup } from '@ver-uds/react';
import { useAuthState } from '@vis-auth/react';
import { Alert } from '@ver-uds/uswds-react';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import { Account, AccountStatus, AccountType, CloseInfo } from 'eve-account-client';
import FullWidthLayout from '../layouts/FullWidthLayout';
import { useAccountStatusUpdateMutation, useClosedReasonsQuery } from '../services';
import AlertConstants from '../constants/AlertConstants';
import { redirectToUrl } from '../utils/util';
import { getVerbiageForTerminationRequest } from '../utils/termination-request';
import ErrorConstants from '../constants/ErrorConstants';
import ErrorBar from './ErrorBar';

const pageTitle = 'Review Termination Request';

type TerminationRequestReviewProps = {
  accountData: Account;
  terminationFormValues: FormValues;
  leaveReviewPage: () => void;
};

type FormValues = {
  closedReasonCode: string;
  closedReasonText: string;
  childClosedReasonCode: string;
  dunsNumber: string;
};

const TerminationRequestReview = ({
  accountData,
  terminationFormValues,
  leaveReviewPage,
}: TerminationRequestReviewProps): JSX.Element => {
  const { user } = useAuthState();
  // eslint-disable-next-line camelcase
  const accountId = user?.account_id as string;

  const accountType = accountData?.type || AccountType.CORPORATE;

  const companyInfoData = accountData?.companyInfo;

  const { data: closedReasons } = useClosedReasonsQuery(accountData?.type);

  const history = useHistory();

  const todayDatePretty = format(new Date(), 'E, MMM d, yyyy');

  const { companyPronoun, companyText, possessiveCompanyText } = getVerbiageForTerminationRequest(1);

  const alertHeading = AlertConstants.TERMINATION_REQUEST_CONFIRM_HEADING.replace(
    /{{ companyPronoun }}/g,
    companyPronoun,
  ).replace('{{ possessiveCompanyText }}', possessiveCompanyText);
  const alertText = AlertConstants.TERMINATION_REQUEST_CONFIRM_TEXT.replace(
    /{{ companyPronoun }}/g,
    companyPronoun,
  ).replace('{{ companyText }}', companyText);

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const closedRequestReasonCode =
    terminationFormValues?.childClosedReasonCode ?? terminationFormValues?.closedReasonCode;
  const closedReasonConfirmationDisplay =
    terminationFormValues?.closedReasonText ||
    closedReasons?.find((f) => f.code === closedRequestReasonCode)?.displayName;

  const [mutate] = useAccountStatusUpdateMutation((error: Error | Response) => {
    setErrorMessage(ErrorConstants.GENERAL_ERROR);
  });

  const onSubmit = async () => {
    const closingInfo: CloseInfo = {
      closedRequestReasonText: terminationFormValues?.closedReasonText ?? undefined,
      closedRequestReasonCode: terminationFormValues?.childClosedReasonCode ?? terminationFormValues?.closedReasonCode,
    };

    const status = AccountStatus.TERMINATIONREQUESTED;
    // only include the dunsNumber if the user entered a value
    // this prevents an empty string value coming in from the field input
    if (terminationFormValues?.dunsNumber) {
      closingInfo.dunsNumber = terminationFormValues.dunsNumber;
    }

    await mutate(
      {
        accountId,
        closingInfo,
        status,
      },
      {},
    );
  };

  const onFormSubmit = async () => {
    try {
      await onSubmit();
      // Route to Termination Request Summary page
    } catch (error) {
      setErrorMessage(ErrorConstants.GENERAL_ERROR);
    }
  };

  return (
    <FullWidthLayout title={pageTitle}>
      <section className="padding-y-4">
        <h1>{pageTitle}</h1>
        <ErrorBar errorMessage={errorMessage} />
        {accountType === AccountType.CORPORATE ? (
          <Alert status="warning" slim>
            <Alert.Text>{alertHeading}</Alert.Text>
          </Alert>
        ) : (
          <Alert status="warning">
            <Alert.Heading>{alertHeading}</Alert.Heading>
            <Alert.Text>{alertText}</Alert.Text>
          </Alert>
        )}
        <FieldGroup className="grid-row grid-gap margin-bottom-neg-3">
          <FieldGroup.Field className="tablet:grid-col-12" name="Company Name">
            {companyInfoData?.companyName ?? '---'}
          </FieldGroup.Field>
          <FieldGroup.Field className="tablet:grid-col-12" name="Company ID">
            {accountId}
          </FieldGroup.Field>
          <FieldGroup.Field className="tablet:grid-col-12" name="DUNS Number">
            {companyInfoData?.dunsNumber || terminationFormValues?.dunsNumber || '---'}
          </FieldGroup.Field>
          <FieldGroup.Field className="tablet:grid-col-12" name="Termination Request Date">
            {todayDatePretty}
          </FieldGroup.Field>
          <FieldGroup.Field className="tablet:grid-col-12" name="Termination Request Reason">
            {closedReasonConfirmationDisplay}
          </FieldGroup.Field>
        </FieldGroup>
        <hr className="margin-top-5" />
        <ButtonGroup className="margin-top-4">
          <Button type="button" variant="secondary" onClick={leaveReviewPage}>
            Back
          </Button>
          <Button type="button" variant="destructive" onClick={onFormSubmit}>
            Yes, Request Termination
          </Button>
          <Button type="button" variant="tertiary" onClick={() => redirectToUrl(history)}>
            Cancel
          </Button>
        </ButtonGroup>
      </section>
    </FullWidthLayout>
  );
};

export default TerminationRequestReview;
